import type { HyperliquidReconnectReason } from '@/lib/utils/exchange-util'
import { TradeVenue } from '@/types/generated/common/types'

/**
 * Where each agent-wallet / session-key venue sends "re-authorize" and
 * "disconnect". One table per venue so a new venue can't silently fall into
 * Hyperliquid's RPCs: an unknown venue gets `null` (no action), never a default.
 */
export type ReauthorizeRoute =
  | 'resume-derive'
  | 'resign-derive'
  | 'resume-lighter'
  | 'relink-lighter'
  | 'resign-papertrade'
  | 'onboard-hyperliquid'

export type DisconnectRoute = 'derive' | 'papertrade' | 'hyperliquid'

export function reauthorizeRoute(tv: TradeVenue, isPendingSetup: boolean): ReauthorizeRoute | null {
  switch (tv) {
    case TradeVenue.TRADE_VENUE_DERIVE:
      return isPendingSetup ? 'resume-derive' : 'resign-derive'
    case TradeVenue.TRADE_VENUE_LIGHTER:
      return isPendingSetup ? 'resume-lighter' : 'relink-lighter'
    case TradeVenue.TRADE_VENUE_PAPERTRADE:
      return 'resign-papertrade'
    case TradeVenue.TRADE_VENUE_HYPERLIQUID:
      return 'onboard-hyperliquid'
    default:
      return null
  }
}

/** Lighter has no disconnect RPC (Delete is its removal path). */
export function disconnectRoute(tv: TradeVenue): DisconnectRoute | null {
  switch (tv) {
    case TradeVenue.TRADE_VENUE_DERIVE:
      return 'derive'
    case TradeVenue.TRADE_VENUE_PAPERTRADE:
      return 'papertrade'
    case TradeVenue.TRADE_VENUE_HYPERLIQUID:
      return 'hyperliquid'
    default:
      return null
  }
}

export type ReconnectBannerKey =
  | 'lighterNeedsReconnect'
  | 'deriveExpiringSoon'
  | 'deriveNeedsReconnect'
  | 'papertradeExpiringSoon'
  | 'papertradeNeedsReconnect'
  | 'hyperliquidExpiringSoon'
  | 'hyperliquidNeedsReconnect'

/** Banner copy key per venue. Lighter has no expiry concept, so it never gets the expiring copy. */
export function reconnectBannerKey(
  tv: TradeVenue,
  reason: HyperliquidReconnectReason | null
): ReconnectBannerKey | null {
  const expiring = reason === 'expiring_soon'
  switch (tv) {
    case TradeVenue.TRADE_VENUE_LIGHTER:
      return 'lighterNeedsReconnect'
    case TradeVenue.TRADE_VENUE_DERIVE:
      return expiring ? 'deriveExpiringSoon' : 'deriveNeedsReconnect'
    case TradeVenue.TRADE_VENUE_PAPERTRADE:
      return expiring ? 'papertradeExpiringSoon' : 'papertradeNeedsReconnect'
    case TradeVenue.TRADE_VENUE_HYPERLIQUID:
      return expiring ? 'hyperliquidExpiringSoon' : 'hyperliquidNeedsReconnect'
    default:
      return null
  }
}
